import React, { useState, useRef, useCallback } from 'react';

/**
 * Dependency-free bottom sheet (mobile "In de buurt" container).
 * Collapsed it peeks above the bottom edge; the grip is a real <button>
 * (tap toggles, keyboard accessible) and also accepts touch-drag. Snap uses
 * the existing non-bouncy brand easing - no spring physics.
 * Touch handlers live ONLY on the grip so the inner list scrolls freely.
 */
function BottomSheet({ peekLabel, expanded, onToggle, children }) {
    const sheetRef = useRef(null);
    const dragState = useRef(null);
    const [dragging, setDragging] = useState(false);

    const onTouchStart = useCallback((e) => {
        const sheet = sheetRef.current;
        if (!sheet) return;
        dragState.current = {
            startY: e.touches[0].clientY,
            startOffset: expanded ? 0 : sheet.getBoundingClientRect().height - getPeekHeight(sheet),
            travel: sheet.getBoundingClientRect().height - getPeekHeight(sheet),
            moved: 0,
        };
        setDragging(true);
    }, [expanded]);

    const onTouchMove = useCallback((e) => {
        const sheet = sheetRef.current;
        const st = dragState.current;
        if (!sheet || !st) return;
        const delta = e.touches[0].clientY - st.startY;
        st.moved = delta;
        const offset = Math.min(Math.max(st.startOffset + delta, 0), st.travel);
        sheet.style.transform = `translateY(${offset}px)`;
    }, []);

    const onTouchEnd = useCallback(() => {
        const sheet = sheetRef.current;
        const st = dragState.current;
        setDragging(false);
        if (!sheet || !st) return;
        sheet.style.transform = '';
        // Snap when dragged past 25% of the travel distance
        const threshold = st.travel * 0.25;
        if (expanded && st.moved > threshold) onToggle(false);
        else if (!expanded && st.moved < -threshold) onToggle(true);
        dragState.current = null;
    }, [expanded, onToggle]);

    return (
        <div
            ref={sheetRef}
            className={`tracker-bottom-sheet ${expanded ? 'expanded' : 'collapsed'} ${dragging ? 'dragging' : ''}`}
        >
            <button
                type="button"
                className="sheet-grip"
                aria-expanded={expanded}
                onClick={() => onToggle(!expanded)}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
            >
                <span className="sheet-grip-bar" aria-hidden="true" />
                <span className="sheet-grip-label">{peekLabel}</span>
            </button>
            <div className="sheet-body">
                {children}
            </div>
        </div>
    );
}

function getPeekHeight(sheet) {
    const grip = sheet.querySelector('.sheet-grip');
    return grip ? grip.getBoundingClientRect().height : 92;
}

export default BottomSheet;
